import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award } from "lucide-react";
import { getSubjectIcon } from "@/components/content/SubjectIcon";
import { MilestoneCertificate } from "@/components/dashboard/MilestoneCertificate";
import { useChapters, useSubjects } from "@/hooks/useCurriculum";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useDashboard } from "@/hooks/useProgress";
import { cn } from "@/lib/cn";
import type { SubjectRollup } from "@/lib/api-types";

export type SubjectProgressProps = {
  classId: string | null;
};

/**
 * Per-subject completion -- the user's touched subjects, each with a thin
 * progress bar. Percentages come from `subject_rollups` on the dashboard
 * response, which is computed over EVERY touched chapter by the same shared
 * aggregation the parent portal reads.
 *
 * It used to be computed here from `recent_chapters`, which is capped at five
 * rows across all subjects combined -- so at most five chapters were ever
 * counted, and "N of M touched" read far below reality. `recent_chapters` is a
 * recency peek, not a sample.
 *
 * Hidden when nothing has been touched -- the resume card already carries the
 * "get started" CTA.
 */
export function SubjectProgress({ classId }: SubjectProgressProps) {
  const dashboard = useDashboard();
  const recent = useMemo(() => dashboard.data?.recent_chapters ?? [], [dashboard.data]);
  const rollups = dashboard.data?.subject_rollups ?? null;
  // See RecentChapters: derive an effective class from the first progress
  // record when the user's profile doesn't have class_id set yet.
  const effectiveClassId = classId ?? recent[0]?.class_id ?? null;
  const subjects = useSubjects(effectiveClassId ?? undefined);

  // Touched subjects, most recently active first. From the rollups when the
  // backend supplies them; otherwise from recent_chapters, which is what a
  // frontend deployed ahead of the Lambda will have to live with.
  const touchedSubjectIds = useMemo(() => {
    if (rollups) {
      return rollups
        .filter((r) => (r.chapters_touched ?? r.chapters_done) > 0 || r.last_activity_at)
        .sort((a, b) => (b.last_activity_at ?? "").localeCompare(a.last_activity_at ?? ""))
        .map((r) => r.subject_id);
    }
    const seen = new Set<string>();
    const out: string[] = [];
    for (const p of recent) {
      if (!seen.has(p.subject_id)) { seen.add(p.subject_id); out.push(p.subject_id); }
    }
    return out;
  }, [rollups, recent]);

  if (touchedSubjectIds.length === 0) return null;
  const visible = touchedSubjectIds.slice(0, 4);

  return (
    <section>
      <header className="flex items-baseline justify-between mb-3">
        <h3 className="font-display text-sm font-semibold tracking-tight text-text-muted uppercase tracking-[0.14em]">
          Subjects
        </h3>
        <Link to="/subjects" className="text-xs text-text-muted hover:text-text">
          See all
        </Link>
      </header>
      <ul className="rounded-2xl border border-border bg-surface overflow-hidden divide-y divide-border">
        {visible.map((sid, i) => {
          const rollup = rollups?.find((r) => r.subject_id === sid) ?? null;
          const looked = rollup?.subject_name
            ?? subjects.data?.find((s) => s.subject_id === sid)?.subject_name;
          // Show a friendly fallback while the subjects list resolves --
          // never the literal subject_id, which leaks "class-11-physics"
          // into the UI.
          const subjectName = looked ?? (subjects.isLoading ? "Loading…" : "Subject");
          return (
            <SubjectRow
              key={sid}
              subjectId={sid}
              subjectName={subjectName}
              rollup={rollup}
              delayIndex={i}
            />
          );
        })}
      </ul>
    </section>
  );
}

function SubjectRow({
  subjectId,
  subjectName,
  rollup,
  delayIndex,
}: {
  subjectId: string;
  subjectName: string;
  /** From the dashboard response. Null only on a pre-rollups backend. */
  rollup: SubjectRollup | null;
  delayIndex: number;
}) {
  const dashboard = useDashboard();
  const me = useCurrentUser();
  const recent = useMemo(() => dashboard.data?.recent_chapters ?? [], [dashboard.data]);
  // Only needed for the legacy path below; the rollup already knows the totals.
  const chaptersQ = useChapters(subjectId, { enabled: rollup === null });
  const [showCertificate, setShowCertificate] = useState(false);

  const { percent, touchedCount, total } = useMemo(() => {
    if (rollup) {
      return {
        percent: rollup.percent_complete,
        touchedCount: rollup.chapters_touched ?? rollup.chapters_done,
        total: rollup.chapters_total,
      };
    }
    // Legacy fallback: a frontend deployed ahead of the Lambda. Capped at the
    // five rows in recent_chapters, and knowingly under-reports.
    const all = chaptersQ.data ?? [];
    const totalCount = all.length;
    if (totalCount === 0) return { percent: 0, touchedCount: 0, total: 0 };
    const idx = new Map(recent.map((p) => [p.chapter_id, p.chapter_completion_percent]));
    let sumPercent = 0;
    let touched = 0;
    for (const ch of all) {
      const p = idx.get(ch.chapter_id);
      // Skip NaN / undefined -- a freshly-opened chapter with no
      // mode_completed yet has no meaningful percent. Counting it as
      // 0 across the whole subject would dilute the visible progress.
      if (Number.isFinite(p)) {
        sumPercent += Math.min(100, Math.max(0, p as number));
        touched++;
      }
    }
    return {
      percent: Math.round(sumPercent / totalCount),
      touchedCount: touched,
      total: totalCount,
    };
  }, [rollup, chaptersQ.data, recent]);

  const Icon = getSubjectIcon(subjectName);

  return (
    <motion.li
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26, delay: delayIndex * 0.04, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={`/subjects/${encodeURIComponent(subjectId)}/chapters`}
        className={cn(
          "block group px-4 py-3.5 sm:py-4",
          "hover:bg-surface-2 active:bg-surface-3 transition-colors",
        )}
      >
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand-soft text-brand flex-shrink-0">
            <Icon className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium text-[15px] truncate group-hover:text-brand transition-colors">
                {subjectName}
              </span>
              <span className="text-xs tabular-nums text-text-muted flex-shrink-0">
                <span className="text-text font-medium">{percent}%</span>
              </span>
            </div>
            <div className="mt-1.5 h-1 rounded-full bg-surface-2 overflow-hidden">
              <motion.div
                className="h-full bg-brand"
                initial={{ width: 0 }}
                animate={{ width: `${percent}%` }}
                transition={{ type: "spring", stiffness: 200, damping: 28, delay: 0.2 + delayIndex * 0.05 }}
              />
            </div>
            <p className="mt-1 text-[11px] text-text-subtle">
              {touchedCount} of {total} chapters touched
            </p>
          </div>
        </div>
      </Link>
      {percent === 100 && total > 0 ? (
        <div className="px-4 pb-3.5 -mt-1.5">
          <button
            type="button"
            onClick={() => setShowCertificate(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-brand hover:underline"
          >
            <Award className="h-3.5 w-3.5" />
            You finished this subject -- view your certificate
          </button>
        </div>
      ) : null}
      {showCertificate ? (
        <MilestoneCertificate
          studentName={me.data?.user_name || "Student"}
          subjectName={subjectName}
          classId={me.data?.class_id ?? null}
          onClose={() => setShowCertificate(false)}
        />
      ) : null}
    </motion.li>
  );
}
